/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import {useEffect} from 'react';
import {useQuery} from 'react-query';
import {loadDevicesBySmartSearch} from '@ion/core/src/services/Connect';
import {GenericObject} from '@ion/core/src/types/entities';
import {
  QueryKey
} from '../config/QueryKeys';
import {
  createQueryResult,
  DurationResult,
  getStaleTime
} from '../utils/helpers';
import {UseDefaultQueryResult} from './types';

interface useDeviceFieldsProps {
  deviceId: string;
  enabled?: boolean;
  staleTime?: DurationResult | number;
}

export type useDeviceFieldsResult = UseDefaultQueryResult & {
  device: GenericObject;
};

/**
 * The useDeviceFields hook is used to get information about a device by passing
 * in the device id (string).
 *
 * ```typescript
 *
 * import useDeviceFields from '@ion/query/src/hooks/useDeviceFields';
 *
 * const {deviceId} = selectedDevice; //8008153
 *
 * const deviceData = useDeviceFields({deviceId: deviceId.toString()});
 *
 * ```
 * @param   deviceId device id for the sc device to query. (string)
 * @param   enabled when true, the query will be executed immediately.
 *          Defaults to false in order to ensure authentication has been completed
 *          prior to issuing the request. (boolean)
 * @param   staleTime DurationResult - Allows for setting the staleTime
 *         for cached query data, defaults to 60 minutes (CTC session timeout duration). (number)
 *
 */
const useDeviceFields = ({
  deviceId = '',
  enabled = false,
  staleTime = getStaleTime(60, false, false)
}: useDeviceFieldsProps) => {
  const query = useQuery([QueryKey.SC_DEVICE_FIELDS, deviceId], async() => {
    if (deviceId?.length > 0) {
      try {
        const {devices} = await loadDevicesBySmartSearch({
          queryString           : `(id:${deviceId})`,
          includeDeviceSettings : true
        });
        return devices[0];
      } catch (error) {
        error('An exception occured while fetching results:', error);
      }
    }
  }, {staleTime: staleTime as number, enabled});

  useEffect(() => {
    if (enabled && !query.data) {
      query.refetch();
    }
  }, [enabled, query]);

  useEffect(function() {
    query.refetch();
  }, [deviceId]);

  return createQueryResult(query, {
    device : query.data || {}
  }) as useDeviceFieldsResult;
};

export default useDeviceFields;
